import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["IconComponent", "disabled", "iconCount", "tabIndex", "value", "readOnly", "showPercentage", "id", "onHoveredValueChange", "onChange"];
import unescapedText from 'I18n/utils/unescapedText';
import { range } from '../../utils/range/range';
import partial from 'react-utils/partial';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { getTabIndex } from '../../utils/TabIndex';
import { DefaultIconComponent, IconToggle } from '../IconToggle/IconToggle';
import { Controllable } from '../Controllable/Controllable';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalAbstractRatingInput = _ref => {
  let {
      IconComponent = DefaultIconComponent,
      disabled = false,
      iconCount = 5,
      tabIndex,
      value,
      readOnly = false,
      showPercentage = false,
      id,
      onHoveredValueChange,
      onChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [focusValue, setFocusValue] = useState(null);
  const onHoveredValueChangeRef = useRef(onHoveredValueChange);
  const isFirstRenderRef = useRef(true);
  useLayoutEffect(() => {
    onHoveredValueChangeRef.current = onHoveredValueChange;
  }, [onHoveredValueChange]);
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      callIfPossible(onHoveredValueChangeRef.current, syntheticEvent(focusValue));
    }
  }, [focusValue]);

  // This should always be the last effect that is run
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
  }, []);
  const getPercentageFilled = index => {
    if (value == null) {
      return 0;
    }
    if (focusValue !== null) {
      // @ts-expect-error - 'focusValue' is possibly 'undefined'
      if (index <= focusValue) {
        return 100;
      }
      return 0;
    }
    if (index <= Math.floor(value)) {
      return 100;
    }
    if (index > Math.ceil(value)) {
      return 0;
    }
    return Math.round(value % 1 * 100);
  };
  const getIsHighlighted = index => {
    if (focusValue !== null) {
      // @ts-expect-error - 'focusValue' is possibly 'undefined'
      return index <= focusValue;
    }

    // @ts-expect-error - 'value' is possibly 'undefined'
    return index <= value;
  };
  const handleChange = newValue => {
    if (disabled || readOnly) {
      return;
    }
    callIfPossible(onChange, syntheticEvent(newValue));
  };
  const handleKeyDown = evt => {
    let nextValue = value || 0;
    if (evt.key === 'ArrowLeft') {
      nextValue = Math.floor(nextValue) <= 0 ? 0 : Math.ceil(nextValue) - 1;
    } else if (evt.key === 'ArrowRight') {
      nextValue = Math.ceil(nextValue) >= iconCount ? iconCount : Math.floor(nextValue) + 1;
    }
    handleChange(nextValue);
  };
  const handleHover = newValue => {
    if (disabled || readOnly) {
      return;
    }
    setFocusValue(newValue);
  };
  return /*#__PURE__*/_jsx("div", {
    id: id,
    "aria-disabled": disabled,
    "aria-valuemax": iconCount,
    "aria-valuemin": 0,
    "aria-valuenow": value || 0,
    "aria-valuetext": unescapedText('salesUI.UIIconRating.ariaValueText', {
      value: value || 0,
      maxValue: iconCount
    }),
    role: "slider",
    tabIndex: getTabIndex(disabled, tabIndex),
    onKeyDown: handleKeyDown,
    children: range(1, iconCount + 1).map(i => /*#__PURE__*/_createElement(IconToggle, Object.assign({}, rest, {
      key: i,
      index: i,
      onChange: partial(handleChange, i),
      onMouseEnter: partial(handleHover, i),
      onMouseLeave: partial(handleHover, null),
      "aria-label": "",
      checked: getIsHighlighted(i),
      readOnly: readOnly,
      percentFilled: showPercentage ? getPercentageFilled(i) : null,
      tabIndex: -1,
      IconComponent: IconComponent,
      active: value === i,
      disabled: disabled,
      focus: i === focusValue
      // @ts-expect-error - 'focusValue' is possibly 'null' or 'undefined'
      ,
      hover: i <= focusValue
    })))
  });
};
export const AbstractRatingInput = Controllable(InternalAbstractRatingInput);